import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Flex,
  Text,
  BigInfoIcon,
  Checkbox,
  Input,
  Button,
  Loader
} from "@theorgchartdev/storybook";
import { appstr } from "@coreapp/utils/appstr";
import { enumShareRoles } from "@coreapp/constants";
import ActionListItem from "@/appComponents/Blocks/Composites/ActionListItem/ActionListItem";
import { ColorSquare } from "./MyHighlights";
import { ShareFilterModal } from "@/appComponents/Blocks/Modals/ShareFilterModal/ShareFilterModal";
import {
  useSharedFilterController,
  type SharedFilterDoc
} from "../FilterPanel/useSharedFilterController";
import { QHighlight } from "@coreapp/CoreApp/QHighlight";
import { GetDoc, GetWSMgr } from "@/Utils/StateLib";
import { useTopic } from "@/hooks/useTopic";
import { TOAST_STATUS, useToast } from "@/providers/ToastProvider";
import { createBoldToastMessage } from "@/appComponents/Blocks/Cards/ToastContainer/utils";
import { chartModeTestId } from "@/appComponents/Navigation/RightSidebar/Utils/chartModeTestId";

type SharedHighlightsProps = {
  onActiveCountChange?: (count: number) => void;
  onEditSharedHighlight?: (highlight: SharedFilterDoc) => void;
  onCopyHighlight?: (definition: any) => void;
  onCopyAndEditHighlight?: (definition: any) => void;
  onViewDetails?: (data: any) => void;
};

export const SharedHighlights = ({
  onActiveCountChange,
  onEditSharedHighlight,
  onCopyHighlight,
  onCopyAndEditHighlight,
  onViewDetails
}: SharedHighlightsProps) => {
  const { sharedHighlights, loading, fetchSharedItems } = useSharedFilterController();
  const { addToast }: any = useToast();
  const [enabledIds, setEnabledIds] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [sortAsc, setSortAsc] = useState(true);
  const [shareModalDoc, setShareModalDoc] = useState<SharedFilterDoc | null>(null);

  useEffect(() => {
    fetchSharedItems("highlight");
  }, []);

  useTopic("chartDocumentClosed", () => {
    setEnabledIds(new Set());
  });

  // Non-owner highlights (what this component manages)
  const nonOwnerHighlights = sharedHighlights.filter(h => h.userRole !== enumShareRoles.owner);

  // Search and sort
  const filteredSorted = useMemo(() => {
    let list = nonOwnerHighlights;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      list = list.filter(h => (h.definition?.name || "").toLowerCase().includes(term));
    }
    return [...list].sort((a, b) => {
      const nameA = (a.definition?.name || "").toLowerCase();
      const nameB = (b.definition?.name || "").toLowerCase();
      return sortAsc ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
    });
  }, [nonOwnerHighlights, searchTerm, sortAsc]);

  // Restore checkbox state from currently active shared highlights on the manager
  useEffect(() => {
    if (nonOwnerHighlights.length === 0) return;
    const doc = GetDoc();
    const highlightMgr = doc?.GetHighlightMgr();
    const activeShared = highlightMgr?.GetSharedHighlights?.() || [];
    const activeNames = new Set(activeShared.map((h: any) => h.GetName?.()));

    const activeIds = new Set<string>();
    nonOwnerHighlights.forEach(h => {
      if (activeNames.has(h.definition?.name)) {
        activeIds.add(h._id);
      }
    });

    if (activeIds.size > 0) {
      setEnabledIds(activeIds);
      onActiveCountChange?.(activeIds.size);
    }
  }, [sharedHighlights]);

  const handleToggle = useCallback(
    (highlight: SharedFilterDoc) => {
      const doc = GetDoc();
      const wsMgr = GetWSMgr();
      const highlightMgr = doc?.GetHighlightMgr();
      if (!highlightMgr || !wsMgr) return;

      const nextEnabledIds = new Set(enabledIds);
      if (nextEnabledIds.has(highlight._id)) {
        nextEnabledIds.delete(highlight._id);
      } else {
        nextEnabledIds.add(highlight._id);
      }
      setEnabledIds(nextEnabledIds);

      // Preserve owner highlights set by "My highlights" tab, rebuild only non-owner highlights
      const existingShared = highlightMgr.GetSharedHighlights?.() || [];
      const managedNames = new Set(nonOwnerHighlights.map(h => h.definition?.name));
      const preservedHighlights = existingShared.filter(
        (h: any) => !managedNames.has(h.GetName?.())
      );

      const newHighlights = nonOwnerHighlights
        .filter(h => nextEnabledIds.has(h._id))
        .map(h => {
          const hl = new QHighlight();
          hl.LoadFromJSON(h.definition);
          hl.SetHighlightApplied(true);
          return hl;
        });

      highlightMgr.SetSharedHighlights([...preservedHighlights, ...newHighlights]);
      onActiveCountChange?.(nextEnabledIds.size);

      wsMgr.ApplyChartHighlights();
      wsMgr.SignalDocChanged();
    },
    [enabledIds, sharedHighlights, onActiveCountChange]
  );

  const getRulesFromDefinition = (definition: any) => {
    const hl = new QHighlight();
    hl.LoadFromJSON(definition);
    const def = hl.GetHighlightRule?.() ?? (hl as any).fDef;
    return def?.GetRulePairList?.() ?? def?.fRulePairList ?? [];
  };

  const getActions = (highlight: SharedFilterDoc) => {
    const actions: any[] = [];
    const role = highlight.userRole;
    const isCollaborator = role === enumShareRoles.collaborator;
    const isOwner = role === enumShareRoles.owner;

    // Info (all roles)
    actions.push({
      iconName: "circle-info" as const,
      tooltipProps: { message: appstr("Highlight details") },
      dataTestId: chartModeTestId("highlight", "shared-highlight-item-info-btn"),
      onClick: () => {
        const sharedHighlight = new QHighlight();
        sharedHighlight.LoadFromJSON(highlight.definition);
        onViewDetails?.({
          name: highlight.definition?.name || "",
          ownerDisplayName: highlight.owner?.displayName || highlight.owner?.username || "",
          isShared: true,
          userRole: highlight.userRole,
          rules: getRulesFromDefinition(highlight.definition),
          sourceTab: "shared",
          shareProps:
            isOwner || isCollaborator
              ? {
                  definition: highlight.definition,
                  filterRef: sharedHighlight,
                  sharedDoc: highlight,
                  onShareComplete: () => fetchSharedItems("highlight")
                }
              : undefined,
          onCopy: () => (onCopyAndEditHighlight ?? onCopyHighlight)?.(highlight.definition),
          onEdit:
            (isOwner || isCollaborator) && onEditSharedHighlight
              ? () => onEditSharedHighlight(highlight)
              : undefined
        });
      }
    });

    // Copy (all roles)
    if (onCopyHighlight) {
      actions.push({
        iconName: "copy" as const,
        tooltipProps: { message: appstr("Copy to My highlights") },
        dataTestId: chartModeTestId("highlight", "shared-highlight-item-copy-btn"),
        onClick: () => {
          onCopyHighlight(highlight.definition);
          addToast({
            ...createBoldToastMessage("Shared highlight copied", {
              entity: highlight.definition?.name ?? ""
            }),
            status: TOAST_STATUS.SUCCESS
          });
        }
      });
    }

    // Share (owner + collaborator)
    if (isOwner || isCollaborator) {
      actions.push({
        iconName: "share-nodes" as const,
        tooltipProps: { message: appstr("Share highlight") },
        dataTestId: chartModeTestId("highlight", "shared-highlight-item-share-btn"),
        onClick: () => setShareModalDoc(highlight)
      });
    }

    // Edit (owner + collaborator)
    if ((isOwner || isCollaborator) && onEditSharedHighlight) {
      actions.push({
        iconName: "pen-to-square" as const,
        tooltipProps: { message: appstr("Edit highlight") },
        dataTestId: chartModeTestId("highlight", "shared-highlight-item-edit-btn"),
        onClick: () => onEditSharedHighlight(highlight)
      });
    }

    // Delete (owner only — handled by parent for owner items in My tab)

    return actions;
  };

  return (
    <Flex direction="column" height="100%">
      <Flex direction="column" padding="16px">
        <Text size="small">{appstr("Shared highlights info")}</Text>
      </Flex>
      {loading ? (
        <Flex direction="column" padding="32px 16px" alignItems="center">
          <Loader.Spinner />
        </Flex>
      ) : nonOwnerHighlights.length === 0 ? (
        <BigInfoIcon
          color="brand"
          name="highlighter-line"
          message={appstr("No shared highlights yet")}
        />
      ) : (
        <>
          <Flex className="sidebar-search-row" gap="8px" alignItems="center" padding="0 16px 8px">
            <Input
              placeholder={appstr("Search highlights")}
              value={searchTerm}
              onInput={(e: any) => setSearchTerm(e.target.value)}
              size="small"
              dataTestId="shared-highlights-search"
            />
            <Button
              iconOnly
              size="small"
              modifier="tertiary"
              iconProps={{ name: "arrow-down-a-z" }}
              tooltipProps={{ message: appstr("Sort") }}
              onClick={() => setSortAsc(prev => !prev)}
              dataTestId={chartModeTestId("highlight", "shared-highlights-sort-toggle")}
            />
          </Flex>
          <Flex
            className="ocn-list-scrollbar"
            flex="1"
            direction="column"
            overflow="auto"
            padding="0 16px 16px"
          >
            {filteredSorted.map((highlight: SharedFilterDoc) => (
              <Flex key={highlight._id} className="ocn-filter-block-list-item">
                <ActionListItem
                  padding={"0px"}
                  innerFlexProps={{ alignItems: "center" }}
                  dataTestId={`shared-highlight-item--${highlight._id}`}
                  actions={getActions(highlight)}
                  label={highlight.definition?.name || "Untitled"}
                  labelProps={{
                    size: "small",
                    modifier: enabledIds.has(highlight._id) ? "bold" : undefined
                  }}
                  contentBefore={
                    <React.Fragment>
                      <Checkbox
                        id={`shared-highlight-enable-${highlight._id}`}
                        checked={enabledIds.has(highlight._id)}
                        size="medium"
                        onToggle={() => handleToggle(highlight)}
                        data-testid={chartModeTestId("highlight", "shared-highlight-item-checkbox")}
                      />
                      <ColorSquare color={highlight.definition?.color || ""} />
                    </React.Fragment>
                  }
                />
              </Flex>
            ))}
          </Flex>
        </>
      )}
      {shareModalDoc &&
        (() => {
          const sharedHighlight = new QHighlight();
          sharedHighlight.LoadFromJSON(shareModalDoc.definition);
          return (
            <ShareFilterModal
              show={true}
              onClose={() => setShareModalDoc(null)}
              filterType="highlight"
              definition={shareModalDoc.definition}
              filterName={shareModalDoc.definition?.name || ""}
              filterRef={sharedHighlight}
              onShareComplete={() => fetchSharedItems("highlight")}
              editMode={true}
              existingFilterId={shareModalDoc._id}
              existingSharedWith={shareModalDoc.sharedWith}
            />
          );
        })()}
    </Flex>
  );
};
